/**
 * ImportOptionsModal
 *
 * The "Choose destination" modal opened from the ReviewImportStep summary
 * phase. It lets the user pick where the imported contacts land and (optionally)
 * which tags to apply, then hands everything to the caller's import handler.
 *
 * Sections (matching the design mockup's ImportOptionsModal):
 *   - Destination: two radio cards — "Import to CRM" (default) and
 *     "Import to a campaign" (also imports to CRM).
 *   - Campaign picker: shown only when destination === 'campaign'. A search
 *     input reveals a results list of REAL campaigns (name + color dot + a
 *     workflow-status badge when present); selecting one shows a chip with a
 *     remove X.
 *   - Add tags (optional): a search input + selectable tag chips (color dot +
 *     Check when selected), sourced from the same `ContactTag` data the import
 *     wizard already uses.
 *
 * Footer: Cancel + "Start import →" (Database icon). "Start import" is disabled
 * while destination === 'campaign' and no campaign has been selected.
 *
 * Real data:
 *   - Campaigns via `api.useGetCampaigns()` (`campaigns?.results`).
 *   - Tags via `api.useGetTags('contacts')` (passed in as `existingTags`).
 */
import { useMemo, useState } from 'react'
import {
  PiCheck,
  PiDatabase,
  PiCircleNotch,
  PiMegaphone,
  PiMagnifyingGlass,
  PiX,
} from 'react-icons/pi'
import { Alert } from 'components/FbUI/Alert'
import { cn } from 'components/UI/cn'
import { FbModal } from 'components/UI/Modal'
import useApiService from 'services/api'
import { useGetCampaignById } from 'features/campaigns/campaigns_api'
import { ContactTag } from 'models/tags'
import { Campaign, WORKFLOW_STATUS_LABELS } from 'models/campaign'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ImportDestination = 'crm' | 'campaign'

export interface ImportOptions {
  destination: ImportDestination
  campaignId: number | null
  campaignName: string | null
  /** Tag names selected in the modal (existing + newly typed). */
  tagNames: string[]
}

export interface ImportOptionsModalProps {
  onClose: () => void
  onConfirm: (opts: ImportOptions) => void
  /** When set, locks the destination to 'campaign' and this campaign ID. */
  lockedCampaignId?: number | null
  /**
   * True when the Map step mapped one or more columns to custom campaign
   * columns. Those are campaign-scoped, so the import must go to a campaign —
   * the "Import to CRM" option is disabled and a note is shown.
   */
  hasCustomColumns?: boolean
}

// ---------------------------------------------------------------------------
// Destination radio options
// ---------------------------------------------------------------------------

const DESTINATION_OPTIONS: {
  value: ImportDestination
  label: string
  description: string
}[] = [
  {
    value: 'crm',
    label: 'Import to CRM',
    description: 'Add contacts to First Bite CRM only',
  },
  {
    value: 'campaign',
    label: 'Import to a campaign',
    description: 'Add to a campaign — also imports to CRM',
  },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Modal for choosing the import destination (CRM vs campaign) plus optional
 * tags. On confirm, calls `onConfirm` with the chosen destination, campaign,
 * and tag names; the caller closes the modal and runs the import.
 */
export function ImportOptionsModal({
  onClose,
  onConfirm,
  lockedCampaignId = null,
  hasCustomColumns = false,
}: ImportOptionsModalProps) {
  const api = useApiService()
  const isLocked = lockedCampaignId != null
  // Custom campaign columns are campaign-scoped, so the import must target a
  // campaign (CRM-only would silently drop them). Force the campaign
  // destination and disable the CRM option when any were mapped.
  const mustUseCampaign = isLocked || hasCustomColumns
  // Only fetch the full campaigns list when NOT in locked mode. In locked mode
  // the picker is hidden; useGetCampaignById (below) fetches the single campaign.
  const { data: campaigns, isLoading: isCampaignsLoading } =
    api.useGetCampaigns(undefined, isLocked)
  const { data: contactTags = [] } = api.useGetTags('contacts')

  // Fetch only the single campaign by id when locked (avoids a full-list
  // fetch that may not include campaigns past page 1 in large workspaces).
  // useGetCampaignById auto-disables when lockedCampaignId is null/0.
  const { data: lockedCampaign = null, isError: isLockedCampaignError } =
    useGetCampaignById(lockedCampaignId)

  // When locked or custom columns are present, destination must be 'campaign';
  // otherwise start at 'crm'.
  const [destination, setDestination] = useState<ImportDestination>(
    mustUseCampaign ? 'campaign' : 'crm'
  )
  const [campaignQuery, setCampaignQuery] = useState('')
  // selectedCampaign is only used in free (unlocked) mode.
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  )
  const [tagQuery, setTagQuery] = useState('')
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())

  // The effective campaign for display/submission.
  const effectiveCampaign = isLocked ? lockedCampaign : selectedCampaign

  const needsCampaign = destination === 'campaign'
  // In locked mode the campaign is fixed, but wait for the single-campaign
  // fetch to resolve before allowing import — otherwise confirming early sends
  // campaignName=null and the success screen shows a blank campaign name.
  // Unblock on fetch error too, so a failed/deleted-campaign lookup can't
  // permanently disable the button: the import still carries the correct
  // campaignId (from the URL) and the backend validates it.
  const canImport = isLocked
    ? lockedCampaign !== null || isLockedCampaignError
    : !needsCampaign || selectedCampaign !== null

  // ---------------------------------------------------------------------------
  // Derived filtered lists
  // ---------------------------------------------------------------------------

  const campaignResults = useMemo(() => {
    const all = campaigns?.results ?? []
    const q = campaignQuery.trim().toLowerCase()
    if (!q) return all
    return all.filter((c) => c.name.toLowerCase().includes(q))
  }, [campaigns, campaignQuery])

  const tagResults: ContactTag[] = useMemo(() => {
    const q = tagQuery.trim().toLowerCase()
    if (!q) {
      // With no query, show only the currently-selected tags as chips.
      return contactTags.filter((t) => selectedTags.has(t.tag))
    }
    return contactTags.filter((t) => t.tag.toLowerCase().includes(q))
  }, [contactTags, tagQuery, selectedTags])

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const toggleTag = (name: string) =>
    setSelectedTags((prev) => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })

  const selectCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign)
    setCampaignQuery('')
  }

  const handleConfirm = () => {
    if (!canImport) return
    if (isLocked) {
      onConfirm({
        destination: 'campaign',
        campaignId: lockedCampaignId,
        campaignName: lockedCampaign?.name ?? null,
        tagNames: Array.from(selectedTags),
      })
      return
    }
    onConfirm({
      destination,
      campaignId: needsCampaign ? (selectedCampaign?.id ?? null) : null,
      campaignName: needsCampaign ? (selectedCampaign?.name ?? null) : null,
      tagNames: Array.from(selectedTags),
    })
  }

  return (
    <FbModal
      open
      onOpenChange={(open: boolean) => {
        if (!open) onClose()
      }}
      title="Import options"
      description="Choose where to import these contacts"
      noPadding
      blockAccept={!canImport}
      onAccept={handleConfirm}
      acceptButtonText=""
      acceptButtonProps={{
        children: (
          <>
            <PiDatabase className="size-4" />
            Start import →
          </>
        ),
      }}
      onCancel={onClose}
      cancelButtonText="Cancel"
    >
      <div className="flex flex-col gap-5 px-5 py-5">
        {/* ── Custom-column notice ── */}
        {hasCustomColumns && (
          <Alert
            color="info"
            icon={PiMegaphone}
            description="Custom campaign columns added — this import must go to a campaign."
          />
        )}

        {/* ── Destination ── */}
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
            Destination
          </p>
          {isLocked ? (
            /* Locked: show a single read-only destination row */
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl border-2 border-brand bg-success-bg-subtle">
              <span className="size-4 rounded-full border-2 border-brand bg-brand flex items-center justify-center shrink-0 mt-0.5">
                <span className="size-1.5 rounded-full bg-white" />
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-semibold text-success-strong">
                  Import to a campaign
                </span>
                <span className="text-xs text-fg-secondary mt-0.5">
                  Add to a campaign — also imports to CRM
                </span>
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {DESTINATION_OPTIONS.map((opt) => {
                const checked = destination === opt.value
                // Custom columns require a campaign, so the CRM option is
                // disabled (it would silently drop the column values).
                const optionDisabled = opt.value === 'crm' && mustUseCampaign
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={optionDisabled}
                    onClick={() => {
                      if (!optionDisabled) setDestination(opt.value)
                    }}
                    className={cn(
                      'flex items-start gap-3 px-4 py-3 rounded-xl border-2 text-left transition-colors',
                      checked
                        ? 'border-brand bg-success-bg-subtle'
                        : 'border-line hover:border-line-form',
                      optionDisabled && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    <span
                      className={cn(
                        'size-4 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors',
                        checked
                          ? 'border-brand bg-brand'
                          : 'border-line bg-surface'
                      )}
                    >
                      {checked && (
                        <span className="size-1.5 rounded-full bg-white" />
                      )}
                    </span>
                    <span className="flex flex-col">
                      <span
                        className={cn(
                          'text-sm font-semibold',
                          checked ? 'text-success-strong' : 'text-fg'
                        )}
                      >
                        {opt.label}
                      </span>
                      <span className="text-xs text-fg-secondary mt-0.5">
                        {opt.description}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* ── Campaign picker (campaign destination only) ── */}
        {needsCampaign && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
              Select campaign
            </p>

            {isLocked ? (
              /* Locked: read-only chip, no search input, no remove button */
              <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-success-bg-subtle border border-success-bg">
                {effectiveCampaign?.color ? (
                  <span
                    className="size-2 rounded-full shrink-0"
                    style={{ backgroundColor: effectiveCampaign.color }}
                  />
                ) : (
                  <PiMegaphone className="size-3.5 shrink-0 text-brand" />
                )}
                <span className="text-sm font-medium text-success-strong flex-1 truncate">
                  {effectiveCampaign?.name ??
                    (isLockedCampaignError
                      ? "Couldn't load campaign name"
                      : 'this campaign')}
                </span>
              </div>
            ) : (
              <>
                <div className="relative">
                  <PiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-fg-muted" />
                  <input
                    type="text"
                    placeholder="Search campaigns..."
                    value={campaignQuery}
                    onChange={(e) => {
                      setCampaignQuery(e.target.value)
                      if (e.target.value) setSelectedCampaign(null)
                    }}
                    aria-label="Search campaigns"
                    className="w-full border border-line-form rounded-xl pl-10 pr-4 py-2.5 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                  />
                </div>

                {campaignQuery.trim() && (
                  <div className="rounded-xl border border-line overflow-hidden max-h-44 overflow-y-auto">
                    {isCampaignsLoading ? (
                      <p className="flex items-center gap-2 px-3 py-3 text-sm text-fg-muted">
                        <PiCircleNotch className="size-4 animate-spin shrink-0" />
                        Loading campaigns...
                      </p>
                    ) : campaignResults.length === 0 ? (
                      <p className="px-3 py-3 text-sm text-fg-muted">
                        No campaigns found.
                      </p>
                    ) : (
                      campaignResults.map((c) => {
                        const statusLabel = c.workflow_status
                          ? WORKFLOW_STATUS_LABELS[c.workflow_status]
                          : undefined
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => selectCampaign(c)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-line last:border-0 hover:bg-surface-hover transition-colors"
                          >
                            {c.color ? (
                              <span
                                className="size-2 rounded-full shrink-0"
                                style={{ backgroundColor: c.color }}
                              />
                            ) : (
                              <PiMegaphone className="size-3.5 shrink-0 text-fg-muted" />
                            )}
                            <span className="text-sm flex-1 truncate text-fg-secondary">
                              {c.name}
                            </span>
                            {statusLabel && (
                              <span className="text-xxs px-1.5 py-0.5 rounded-full border font-medium shrink-0 bg-success-bg-subtle text-success-strong border-success-bg">
                                {statusLabel}
                              </span>
                            )}
                          </button>
                        )
                      })
                    )}
                  </div>
                )}

                {selectedCampaign && !campaignQuery.trim() && (
                  <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-success-bg-subtle border border-success-bg">
                    {selectedCampaign.color ? (
                      <span
                        className="size-2 rounded-full shrink-0"
                        style={{ backgroundColor: selectedCampaign.color }}
                      />
                    ) : (
                      <PiMegaphone className="size-3.5 shrink-0 text-brand" />
                    )}
                    <span className="text-sm font-medium text-success-strong flex-1 truncate">
                      {selectedCampaign.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedCampaign(null)}
                      className="text-fg-secondary hover:text-fg transition-colors"
                      aria-label={`Remove campaign ${selectedCampaign.name}`}
                    >
                      <PiX className="size-3.5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Add tags (optional) ── */}
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
            Add tags{' '}
            <span className="normal-case font-normal text-fg-muted">
              (optional)
            </span>
          </p>
          <div className="relative">
            <PiMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-fg-muted" />
            <input
              type="text"
              placeholder="Search tags..."
              value={tagQuery}
              onChange={(e) => setTagQuery(e.target.value)}
              aria-label="Search tags"
              className="w-full border border-line-form rounded-xl pl-10 pr-4 py-2.5 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
            />
          </div>
          {tagResults.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tagResults.map((tag) => {
                const isSelected = selectedTags.has(tag.tag)
                return (
                  <button
                    key={tag.tag}
                    type="button"
                    onClick={() => toggleTag(tag.tag)}
                    className={cn(
                      'inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border font-medium transition-colors',
                      isSelected
                        ? 'bg-success-bg-subtle text-success-strong border-success-bg'
                        : 'bg-surface text-fg-secondary border-line hover:border-line-form'
                    )}
                  >
                    {isSelected && tag.color ? (
                      <span
                        className="size-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                    ) : (
                      <span className="size-1.5 rounded-full shrink-0 bg-fg-muted" />
                    )}
                    {tag.tag}
                    {isSelected && <PiCheck className="size-3" />}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </FbModal>
  )
}
